import { MessageSquare, Plus, Search, Trash2, X } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

import { IChat } from '@/MODELS/chat'

import TransitionPanel from '../TransitionPanel'

export interface IChatSidebar {
    sidebarOpen: boolean
    setActiveChat: (chatId: string | null) => void
    setSidebarOpen: (open: boolean) => void
    searchQuery: string
    filteredHistory: IChat[]
    setSearchQuery: (query: string) => void
    handleChatSelect: (chatId: string) => void
    handleDeleteChat: (ce: React.SyntheticEvent, chatId: string) => void
    activeChat: IChat
}

export default function ChatSidebar(props: IChatSidebar) {
    const {
        sidebarOpen,
        setActiveChat,
        setSidebarOpen,
        searchQuery,
        filteredHistory,
        setSearchQuery,
        handleChatSelect,
        handleDeleteChat,
        activeChat,
    } = props

    function handleChatSelectAndClose(chatId: string) {
        handleChatSelect(chatId)

        if (window.innerWidth < 1024) {
            setSidebarOpen(false)
        }
    }

    function handleStartNewChat() {
        setActiveChat(null)
        setSidebarOpen(false)
    }

    const sidebarContent = (
        <div className='flex flex-col h-full bg-white border-l px-1'>
            <div className='flex items-center justify-between p-4 border-b border-gray-100'>
                <h2 className='text-lg font-semibold text-gray-900'>Chats</h2>
                <div className='flex items-center gap-2'>
                    <button
                        onClick={handleStartNewChat}
                        className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
                        title='New Chat'
                    >
                        <Plus className='w-5 h-5 text-gray-600' />
                    </button>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className='lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors'
                        aria-label='Close sidebar'
                    >
                        <X className='w-5 h-5 text-gray-600' />
                    </button>
                </div>
            </div>

            <div className='p-4 border-b border-gray-100'>
                <div className='relative'>
                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400' />
                    <input
                        type='text'
                        placeholder='Search conversations...'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className='w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                    />
                </div>
            </div>

            <div className='flex-1 overflow-y-auto'>
                {filteredHistory.length > 0 ? (
                    <div className='p-2 space-y-1'>
                        {filteredHistory.map((chatItem) => (
                            <div
                                key={chatItem._id}
                                onClick={() => handleChatSelectAndClose(chatItem._id)}
                                className={`group relative flex items-start p-3 rounded-lg cursor-pointer transition-all ${
                                    activeChat?._id === chatItem._id
                                        ? 'bg-brand-50 border border-brand-200'
                                        : 'hover:bg-gray-50'
                                }`}
                            >
                                <div className='flex items-center flex-1 min-w-0 gap-3'>
                                    <div
                                        className={`w-2 h-2 rounded-full flex-shrink-0 mt ${
                                            activeChat?._id === chatItem._id ? 'bg-brand-500' : 'bg-gray-300'
                                        }`}
                                    />
                                    <div className='flex-1 min-w-0'>
                                        <p className='text-sm font-medium text-gray-900 truncate'>
                                            {formatChatTitle(chatItem.messages)}
                                        </p>
                                        <p className='text-xs text-gray-500 mt-1'>
                                            {formatDistanceToNow(chatItem.updatedAt || chatItem.createdAt)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => handleDeleteChat(e, chatItem._id)}
                                    className='text-gray-400 p-1.5 hover:bg-red-50 hover:text-red-600 rounded-md transition-all flex-shrink-0'
                                >
                                    <Trash2 className='w-4 h-4' />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='flex flex-col items-center justify-center h-48 text-gray-500 px-6'>
                        <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                            <MessageSquare className='w-8 h-8 text-gray-400' />
                        </div>
                        <p className='text-sm font-medium text-center'>
                            {searchQuery ? 'No matching conversations' : 'No chat history yet'}
                        </p>
                        <p className='text-xs text-gray-400 text-center mt-1'>
                            {searchQuery ? 'Try a different search term' : 'Start a conversation to see it here'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )

    return (
        <>
            <div className='hidden lg:flex w-80 flex-shrink-0 border-r border-gray-200'>{sidebarContent}</div>

            {sidebarOpen && (
                <>
                    <TransitionPanel
                        open={sidebarOpen}
                        from={'left'}
                        setOpen={setSidebarOpen}
                        positionClass='w-[85%] sm:w-[320px]'
                    >
                        {sidebarContent}
                    </TransitionPanel>
                </>
            )}
        </>
    )
}

const formatChatTitle = (messages) => {
    if (messages && messages.length > 0) {
        const firstMessage = messages.find((msg) => msg.role === 'user')
        if (firstMessage) {
            return firstMessage.content.slice(0, 28) + (firstMessage.content.length > 28 ? '...' : '')
        }
    }
    return 'New Chat'
}
